import React, { forwardRef, useState, useEffect, useRef } from 'react';

import { Container, OptionList, OptionStyled, SearchContainer } from './DropdownMenu.styled';
import { MagnifyingGlass } from 'assets/icons/Icons';
import { TextInput } from 'components/v2/Inputs/TextInput/TextInput';
import { escapeRegex } from 'kb-shared/utilities/regex';

import { Props, Option } from './DropdownMenu.types';

export const DropdownMenu = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    id,
    searchable,
    searchPlaceholder,
    onSelect,
    onClose,
    searchFn,
    selected,
    options,
    positionStatic,
    maxMenuHeight
  } = props;
  const isSelected = (option: Option): boolean => {
    return (
      selected?.some(
        selectedOption =>
          selectedOption.value === option.value && selectedOption.label === option.label
      ) ?? false
    );
  };

  const [value, setValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const optionListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the selected option or first option when dropdown opens
    const timer = setTimeout(() => {
      const selectedEl = optionListRef.current?.querySelector(
        '[aria-selected="true"]'
      ) as HTMLElement;
      const firstEl = optionListRef.current?.querySelector('button') as HTMLElement;
      (selectedEl || firstEl)?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const search = (term: string): void => {
    if (!term) return setFilteredOptions(options);

    if (searchFn) {
      const filteredByTerm = searchFn(options, term);
      setFilteredOptions(filteredByTerm);
    } else {
      const regex = new RegExp(`.*${escapeRegex(term)}.*`, 'i');
      const filteredByTerm = options.filter(option => regex.test(option.label));
      setFilteredOptions(filteredByTerm);
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement;
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (e.currentTarget as HTMLElement).previousElementSibling as HTMLElement;
      prev?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  return (
    <Container
      id={id}
      role="listbox"
      ref={ref}
      $positionStatic={positionStatic}
      $maxMenuHeight={maxMenuHeight}
    >
      {searchable && (
        <SearchContainer>
          <TextInput
            id={`input-dropdown-search-${id}`}
            leftIcon={<MagnifyingGlass type="solid" />}
            type="search"
            placeholder={searchPlaceholder}
            value={value}
            onChange={e => {
              setValue(e.currentTarget.value);
              search(e.currentTarget.value);
            }}
          />
        </SearchContainer>
      )}

      <OptionList ref={optionListRef}>
        {filteredOptions.map(option => (
          <OptionStyled
            onClick={() => onSelect(option)}
            onKeyDown={e => handleOptionKeyDown(e)}
            key={`${option.value} ${option.label}`}
            role="option"
            aria-selected={isSelected(option)}
          >
            {option.label}
          </OptionStyled>
        ))}
      </OptionList>
    </Container>
  );
});

// must be set because of forwardRef otherwise eslint complains
DropdownMenu.displayName = 'DropdownMenu';
